import { useEffect, useState } from "react";
import { Sun, Moon, Clock, PanelLeftOpen, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { mergeDisplayPrefs, saveDisplayPrefs, applyTheme } from "@/lib/displayPrefs";

const THEME_MODES = [
  { v: "light", label: "Terang", icon: Sun, hint: "Selalu tampilan terang." },
  { v: "dark", label: "Gelap", icon: Moon, hint: "Selalu tampilan gelap." },
  { v: "auto", label: "Otomatis", icon: Clock, hint: "Gelap otomatis pada jam yang Anda atur." },
];

const SIDEBAR_MODES = [
  { v: "auto", label: "Otomatis (hover)", hint: "Tertutup default; terbuka saat kursor di atasnya." },
  { v: "manual", label: "Manual", hint: "Buka/tutup hanya lewat tombol — tanpa auto-hover." },
  { v: "schedule", label: "Jadwal", hint: "Terbuka pada rentang jam yang Anda atur." },
];

/**
 * AppearanceSettingsForm — form tema + perilaku sidebar per-user.
 * Dipakai di DisplaySettingsDialog (popup user menu) dan Settings → tab Tampilan.
 */
export default function AppearanceSettingsForm({ prefs, onSaved, onCancel }) {
  const [form, setForm] = useState(() => mergeDisplayPrefs(prefs));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(mergeDisplayPrefs(prefs)); }, [prefs]);

  const setTheme = (patch) => setForm((f) => ({ ...f, theme: { ...f.theme, ...patch } }));
  const setSidebar = (patch) => setForm((f) => ({ ...f, sidebar: { ...f.sidebar, ...patch } }));

  const save = async () => {
    setBusy(true);
    try {
      const merged = await saveDisplayPrefs(form);
      applyTheme(merged);
      onSaved?.(merged);
      toast.success("Pengaturan tampilan tersimpan");
    } catch {
      toast.error("Gagal menyimpan pengaturan tampilan");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="appearance-settings-form">
      <section className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Tema Aplikasi</div>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" data-testid="ds-theme-group">
          {THEME_MODES.map((m) => (
            <button key={m.v} type="button" onClick={() => setTheme({ mode: m.v })}
              data-testid={`ds-theme-${m.v}`} title={m.hint}
              className={`border p-3 text-left transition-colors ${form.theme.mode === m.v ? "border-[#0B1D3A] bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
              <m.icon size={16} className={form.theme.mode === m.v ? "text-[#0B1D3A]" : "text-slate-400"} />
              <div className="mt-1.5 text-xs font-semibold text-[#0B1D3A]">{m.label}</div>
              <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{m.hint}</div>
            </button>
          ))}
        </div>
        {form.theme.mode === "auto" && (
          <div className="flex items-center gap-2 text-xs text-slate-600 border border-slate-200 bg-slate-50 px-3 py-2 flex-wrap" data-testid="ds-theme-schedule">
            <span className="shrink-0">Mode gelap aktif dari</span>
            <input type="time" value={form.theme.darkStart} onChange={(e) => setTheme({ darkStart: e.target.value })}
              data-testid="ds-theme-dark-start" className="border border-slate-300 px-2 py-1 bg-white" />
            <span>sampai</span>
            <input type="time" value={form.theme.darkEnd} onChange={(e) => setTheme({ darkEnd: e.target.value })}
              data-testid="ds-theme-dark-end" className="border border-slate-300 px-2 py-1 bg-white" />
            <span className="text-[10px] text-slate-400">(melewati tengah malam didukung)</span>
          </div>
        )}
      </section>

      <section className="space-y-2 mt-4">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Sidebar Menu Kiri</div>
        <div className="space-y-1.5" role="radiogroup" data-testid="ds-sidebar-group">
          {SIDEBAR_MODES.map((m) => (
            <button key={m.v} type="button" onClick={() => setSidebar({ mode: m.v })}
              data-testid={`ds-sidebar-${m.v}`}
              className={`w-full border px-3 py-2 text-left flex items-start gap-2.5 transition-colors ${form.sidebar.mode === m.v ? "border-[#0B1D3A] bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
              <PanelLeftOpen size={14} className={`mt-0.5 shrink-0 ${form.sidebar.mode === m.v ? "text-[#0B1D3A]" : "text-slate-400"}`} />
              <span>
                <span className="block text-xs font-semibold text-[#0B1D3A]">{m.label}</span>
                <span className="block text-[10px] text-slate-500 leading-tight mt-0.5">{m.hint}</span>
              </span>
            </button>
          ))}
        </div>
        {form.sidebar.mode !== "manual" && (
          <div className="border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-2" data-testid="ds-sidebar-autohide-box">
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={form.sidebar.autoHide !== false}
                onChange={(e) => setSidebar({ autoHide: e.target.checked })}
                data-testid="ds-sidebar-autohide" className="accent-[#0B1D3A] w-3.5 h-3.5" />
              <span className="font-semibold">Auto-hide — sembunyikan otomatis saat kursor keluar</span>
            </label>
            {form.sidebar.autoHide !== false && (
              <div className="flex items-center gap-2 text-xs text-slate-600 flex-wrap" data-testid="ds-sidebar-hide-delay-row">
                <span className="shrink-0">Sidebar tersembunyi setelah</span>
                <select value={String(form.sidebar.hideDelaySec ?? 0.3)}
                  onChange={(e) => setSidebar({ hideDelaySec: Number(e.target.value) })}
                  data-testid="ds-sidebar-hide-delay" className="border border-slate-300 px-2 py-1 bg-white">
                  <option value="0.3">Segera (0,3 detik)</option>
                  <option value="1">1 detik</option>
                  <option value="2">2 detik</option>
                  <option value="3">3 detik</option>
                  <option value="5">5 detik</option>
                  <option value="10">10 detik</option>
                  <option value="30">30 detik</option>
                </select>
                <span className="text-[10px] text-slate-400">kursor keluar dari area sidebar</span>
              </div>
            )}
            <div className="text-[10px] text-slate-400">Matikan auto-hide agar sidebar tetap terbuka setelah disentuh kursor.</div>
          </div>
        )}
        {form.sidebar.mode === "schedule" && (
          <div className="flex items-center gap-2 text-xs text-slate-600 border border-slate-200 bg-slate-50 px-3 py-2 flex-wrap" data-testid="ds-sidebar-schedule">
            <span className="shrink-0">Sidebar terbuka dari</span>
            <input type="time" value={form.sidebar.openStart} onChange={(e) => setSidebar({ openStart: e.target.value })}
              data-testid="ds-sidebar-open-start" className="border border-slate-300 px-2 py-1 bg-white" />
            <span>sampai</span>
            <input type="time" value={form.sidebar.openEnd} onChange={(e) => setSidebar({ openEnd: e.target.value })}
              data-testid="ds-sidebar-open-end" className="border border-slate-300 px-2 py-1 bg-white" />
          </div>
        )}
      </section>

      <div className="flex justify-end gap-2 mt-5">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} data-testid="ds-cancel" className="rounded-none">Batal</Button>
        )}
        <Button type="button" onClick={save} disabled={busy} data-testid="ds-save"
          className="rounded-none bg-[#0B1D3A] hover:bg-[#12284d] text-white uppercase text-xs tracking-wider font-semibold">
          <Save size={13} className="mr-1.5" /> {busy ? "Menyimpan…" : "Simpan"}
        </Button>
      </div>
    </div>
  );
}
